import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  useGetMyAccount,
  useGetMyProfile,
  useGetMySongsDetailedPaginated,
} from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import { hasAdminRights } from "@/auth/shared";
import H2 from "@/components/H2";
import LoadingSmall from "@/components/LoadingSmall";
import RebrandButtonLink from "@/components/RebrandButtonLink";
import Spacer from "@/components/Spacer";
import useGetColours from "@/helpers/getColours";
import { sendProperty } from "@/helpers/helpers";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import SongsTable from "./SongsTable";
import SongSuggestions from "./SongSuggestions";

function Songs() {
  const { t } = useTranslation("songs");
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfigAuth();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const isRoughTrade = siteConfig?.display_name?.includes(
    "Rough Trade Publishing",
  );
  useEffect(() => {
    if (isRoughTrade) {
      navigate({ to: "/statements" });
    }
  }, [isRoughTrade, navigate]);

  const flags = useFlags();
  const newAddSongs = flags["pd-add-songs-rework"];

  const profile = useGetMyProfile();

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const { highlightColour } = useGetColours();
  // const tableState = AppState.useState(s => s.songsTable);
  const tableState = useAppStore(s => s.songsTable);

  const songs = useGetMySongsDetailedPaginated({
    aac: selectedAccount,
    perPage: tableState.perPage,
    page: tableState.page,
    sortBy: tableState.sortBy?.[0]?.id,
    desc: tableState.sortBy?.[0]?.desc,
    globalFilter: tableState.globalFilter,
  });

  const adminRights = hasAdminRights(myAccount?.data);

  // Remove the aggressive refetch effect - React Query will handle this automatically
  // when the query key dependencies change

  useEffect(() => {
    if (selectedAccount) {
      // Reset the filter and pagination when switching accounts
      useAppStore.setState(state => ({
        songsTable: {
          ...state.songsTable,
          globalFilter: null,
          page: 1,
        },
      }));
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (songs?.isSuccess && songs?.data?.works !== null) {
      sendProperty({
        property: "songs",
        value: songs.data.works.length,
        email: profile?.data?.email,
      });
    }
  }, [songs, profile]);

  return (
    <>
      <div className="flex flex-wrap w-full">
        <div className="w-full lg:min-h-screen-minus-header ">
          <div className={`${newAddSongs ? "" : "mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40"}`}>
            <div className="flex flex-wrap items-baseline justify-between">
              <div>
                <H2>{t("songs:songs-header")}</H2>
              </div>
              <div>
                <RebrandButtonLink
                  plusIcon
                  id="btnSongsAddNew"
                  onClick={
                    adminRights
                      ? () => navigate({ to: "/songs/create" })
                      : undefined
                  }
                >
                  {t("songs:add-new-song-button")}
                </RebrandButtonLink>
              </div>
            </div>
            <Spacer />
            {!siteConfig?.disable_suggestions && flags["pd-spotify-suggestions-feature"] && (
              <>
                <div
                  className={`${
                    isTunecore ? "bg-tunecore-dark-element" : " bg-white"
                  } rounded-lg p-4 lg:p-8`}
                >
                  <div className="flex items-center">
                    <h2 className="text-lg font-semibold">
                      {isTunecore
                        ? t("songs:suggestions-header")
                        : t("songs:spotify-header")}
                    </h2>
                  </div>
                  <Spacer thin />
                  <SongSuggestions highlightColour={highlightColour} />
                </div>
              </>
            )}
            <Spacer />
            <div
              className={`${
                isTunecore ? "bg-tunecore-dark-element" : " bg-white"
              } rounded-lg p-4 lg:p-8`}
            >
              <div className="flex flex-col justify-center gap-2">
                <h2 className="text-lg font-semibold">
                  {t("songs:song-library-header")}
                </h2>
              </div>
              <Spacer thin />
              {!songs.isSuccess && <LoadingSmall />}
              {songs.isSuccess && (
                <SongsTable
                  highlightColour={highlightColour}
                  data={songs?.data?.works || []}
                  total={songs?.data?.total || 0}
                />
              )}
            </div>
            <Spacer />
          </div>
        </div>
      </div>
    </>
  );
}
export default Songs;
